import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="turbo-streams"
export default class extends Controller {
  static targets = ["content", "counter", "status"]
  static values = { 
    refreshUrl: String,
    refreshInterval: Number,
    autoRefresh: Boolean 
  }

  connect() {
    console.log("Turbo Streams controller connected")
    
    if (this.autoRefreshValue) {
      this.startAutoRefresh()
    }
  }

  disconnect() {
    this.stopAutoRefresh()
  }

  // Manual refresh trigger
  refresh() {
    if (this.refreshUrlValue) {
      fetch(this.refreshUrlValue, {
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      .then(response => response.text())
      .then(html => {
        Turbo.renderStreamMessage(html)
      })
      .catch(error => {
        console.error("Turbo refresh failed:", error)
        this.showError("Failed to refresh content")
      })
    }
  }

  // Update counter displays
  updateCounter(event) {
    const { count, target } = event.detail
    const counterTarget = this.counterTargets.find(el => 
      el.dataset.counterType === target
    )
    
    if (counterTarget) {
      counterTarget.textContent = count
      this.animateUpdate(counterTarget)
    }
  }

  // Update status indicators
  updateStatus(event) {
    const { status, target } = event.detail
    const statusTarget = this.statusTargets.find(el => 
      el.dataset.statusType === target
    )
    
    if (statusTarget) {
      statusTarget.className = this.getStatusClasses(status)
      statusTarget.textContent = status.charAt(0).toUpperCase() + status.slice(1)
    }
  }

  // Handle form submissions with Turbo
  submitForm(event) {
    const form = event.target.closest('form')
    if (form) {
      // Add loading state
      this.setLoadingState(true)
      
      // Let Turbo handle the submission
      form.addEventListener('turbo:submit-end', () => {
        this.setLoadingState(false)
      }, { once: true })
    }
  }

  // Auto-refresh functionality
  startAutoRefresh() {
    if (this.refreshIntervalValue > 0) {
      this.refreshTimer = setInterval(() => {
        this.refresh()
      }, this.refreshIntervalValue * 1000)
    }
  }

  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  // Helper methods
  animateUpdate(element) {
    element.classList.add('animate-pulse')
    setTimeout(() => {
      element.classList.remove('animate-pulse')
    }, 1000)
  }

  getStatusClasses(status) {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
    
    switch(status) {
      case 'active':
      case 'connected':
      case 'success':
        return `${baseClasses} bg-green-900 text-green-300`
      case 'paused':
      case 'pending':
      case 'warning':
        return `${baseClasses} bg-yellow-900 text-yellow-300`
      case 'error':
      case 'failed':
      case 'disconnected':
        return `${baseClasses} bg-red-900 text-red-300`
      default:
        return `${baseClasses} bg-gray-700 text-gray-300`
    }
  }

  setLoadingState(loading) {
    const loadingElements = this.element.querySelectorAll('[data-loading]')
    loadingElements.forEach(el => {
      if (loading) {
        el.classList.add('opacity-50', 'pointer-events-none')
      } else {
        el.classList.remove('opacity-50', 'pointer-events-none')
      }
    })
  }

  showError(message) {
    // Dispatch custom event for error handling
    this.dispatch("error", { detail: { message } })
  }
} 